import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib";
import { isEmpty } from "@/utils";

interface TProps {
    dates: any;
    selectedDate: number;
    handleSelectDate: (dateObj: any) => void;
}

const Dates = ({ dates, selectedDate, handleSelectDate }: TProps) => {
    const date = new Date(Date.now());
    const isDateDisabled = (calendarDate: Date) => {
        const targetTimestamp = new Date(calendarDate).setHours(0, 0, 0, 0);
        return !dates.some((d: any) => {
            const availableDate = new Date(d.date).setHours(0, 0, 0, 0);
            return availableDate === targetTimestamp;
        });
    };
    const disablePreviousDates = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    return dates?.length > 10 ? (
        <div className='flex w-full items-center justify-center'>
            <Calendar
                selected={new Date(selectedDate)}
                startMonth={date < new Date(dates[0]) ? new Date(dates[0]) : date}
                mode='single'
                defaultMonth={date}
                onSelect={(selectedDate) => {
                    if (!selectedDate) return;

                    const dateObj = dates.find((d: any) =>
                        new Date(d.date).toDateString() === selectedDate.toDateString()
                    );
                    if (dateObj) {
                        handleSelectDate(dateObj);
                    }
                }}
                className='w-full rounded-xl border p-4'
                classNames={{
                    button_previous:
                        'bg-gray-200 rounded-lg p-2 [&_svg]:text-gray-600 hover:bg-gray-300',
                    button_next:
                        'bg-gray-200 rounded-lg p-2 [&_svg]:text-gray-600 hover:bg-gray-300',
                    today:
                        'text-accent-foreground rounded-md data-[selected=true]:rounded-none',
                    week: 'flex w-full mt-2 md:h-10',
                    dropdowns:
                        'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
                }}
                captionLayout='dropdown'
                disabled={[disablePreviousDates, isDateDisabled]}
            />
        </div>
    ) : (
        <div className='flex max-w-3xl gap-4 overflow-x-auto pb-4'>
            {!isEmpty(dates) ?
                dates?.map((dateObject: any, index: number) => {
                    const d = new Date(dateObject?.date);
                    const dayNumber = d.getDate();
                    const weekday = d
                        .toLocaleString('en-US', { weekday: 'short' })
                        .toUpperCase();
                    const month = d
                        .toLocaleString('en-US', { month: 'short' })
                        .toUpperCase();
                    const year = d
                        .toLocaleString('en-US', { year: 'numeric' })
                        .toUpperCase();

                    return (
                        <div
                            key={index}
                            onClick={() => {
                                handleSelectDate(dateObject);
                            }}
                            className={cn(
                                'group flex h-22 w-15 shrink-0 cursor-pointer flex-col items-center rounded-lg border border-gray-200 bg-white transition-colors',
                                'hover:border-zinc-800 hover:bg-zinc-900 hover:text-white',
                                selectedDate === dateObject?.date && 'border-zinc-800 bg-zinc-900 text-white',
                            )}
                        >
                            <p className='w-full border-b-2 border-dashed border-gray-200 py-1 text-center text-[10px] font-medium text-zinc-500 transition-colors group-hover:border-zinc-700 group-hover:text-white'>
                                {month} {year}
                            </p>
                            <p className='mt-1 text-xl font-bold'>{dayNumber}</p>
                            <p className='mt-1 text-[10px] font-medium'>{weekday}</p>
                        </div>
                    );
                }) : (
                    <div className='w-full mt-4 text-center text-gray-500'>No dates available</div>
                )}
        </div>
    )
}

export default Dates;